import { CoreModule, ListService } from '@abp/ng.core';
import { GdprConfigModule } from '@volo/abp.ng.gdpr/config';
import { SettingManagementConfigModule } from '@abp/ng.setting-management/config';
import { InternetConnectionStatusComponent, ThemeSharedModule, ToasterService } from '@abp/ng.theme.shared';
import {NgModule, isDevMode, ErrorHandler} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommercialUiConfigModule } from '@volo/abp.commercial.ng.ui/config';
import { AccountAdminConfigModule } from '@volo/abp.ng.account/admin/config';
import { AccountPublicConfigModule } from '@volo/abp.ng.account/public/config';
import { AuditLoggingConfigModule } from '@volo/abp.ng.audit-logging/config';
import { IdentityConfigModule } from '@volo/abp.ng.identity/config';
import { LanguageManagementConfigModule } from '@volo/abp.ng.language-management/config';
import { registerLocale, storeLocaleData } from '@abp/ng.core/locale';
import { SaasConfigModule } from '@volo/abp.ng.saas/config';
import { TextTemplateManagementConfigModule } from '@volo/abp.ng.text-template-management/config';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { APP_ROUTE_PROVIDER } from './route.provider';
import { OpeniddictproConfigModule } from '@volo/abp.ng.openiddictpro/config';
import { FeatureManagementModule } from '@abp/ng.feature-management';
import { AbpOAuthModule } from '@abp/ng.oauth';
import { ThemeLeptonXModule } from '@volosoft/abp.ng.theme.lepton-x';
import { TopMenuLayoutModule } from '@volosoft/abp.ng.theme.lepton-x/layouts';
import { ROTAS_ROTA_ROUTE_PROVIDER } from './rotas/rota/providers/rota-route.provider';
import { OFERTAS_OFERTA_ROUTE_PROVIDER } from './ofertas/oferta/providers/oferta-route.provider';
import { NgSelectModule } from '@ng-select/ng-select';
import { MANAGE_PROFILE_TAB_PROVIDER } from './galileu-credentials/galileu-credential/providers/galileu-credential-tab.provider';
import { USUARIOS_ACESSOS_TAB_PROVIDER } from './usuarios-acessos/usuarios-acessos-tab.provider';
import { GALILEU_CREDENTIALS_GALILEU_CREDENTIAL_ROUTE_PROVIDER } from './galileu-credentials/galileu-credential/providers/galileu-credential-route.provider';
import { GalileuCredentialDetailViewService } from './galileu-credentials/galileu-credential/services/galileu-credential-detail.service';
import { GalileuCredentialViewService } from './galileu-credentials/galileu-credential/services/galileu-credential.service';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { TitleStrategy } from '@angular/router';
import { AcceptLanguageInterceptor } from './language-interceptor';
import { GlobalErrorHandler } from './observability/global-error-handler';
import { ErrorReportingInterceptor } from './observability/error-reporting.interceptor';
import { OFERTAS_ABERTAS_ROUTE_PROVIDER } from './ofertas-abertas/providers/ofertas-abertas-route.provider';
import { OFERTA_RECORDS_OFERTA_RECORD_ROUTE_PROVIDER } from './oferta-records/oferta-record/providers/oferta-record-route.provider';
import { API_STATUSES_API_STATUS_ROUTE_PROVIDER } from './api-statuses/api-status/providers/api-status-route.provider';
import { ServiceWorkerModule } from '@angular/service-worker';
import { Capacitor } from '@capacitor/core';
import { IonicModule } from '@ionic/angular';
import { WHATS_APP_CONTACTS_WHATS_APP_CONTACT_ROUTE_PROVIDER } from './whats-app-contacts/whats-app-contact/providers/whats-app-contact-route.provider';
import { VIAGEM_ROUTE_PROVIDER } from './viagens/providers/viagem-route.provider';
import { GERENCIAMENTO_VIAGEM_ROUTE_PROVIDER } from './gerenciamento-viagens/providers/gerenciamento-viagem-route.provider';
import { FLEET_SCORE_ROUTE_PROVIDER } from './fleet-score/providers/fleet-score-route.provider';
import { CONFIRMACAO_ENTREGAS_ROUTE_PROVIDER } from './confirmacao-entregas/providers/confirmacao-entregas-route.provider';
import { PROFILE_PICTURE_ROUTE_PROVIDER } from './profile-picture/providers/profile-picture-route.provider';
import { FLOWPAY_ROUTE_PROVIDER } from './flowpay/providers/flowpay-route.provider';
import { FATURAMENTO_ROUTE_PROVIDER } from './faturamento/providers/faturamento-route.provider';
import { BOLSAO_ROUTE_PROVIDER } from './bolsao/providers/bolsao-route.provider';
import { SaasModule } from '@volo/abp.ng.saas';
import { ToastrModule } from 'ngx-toastr';

import(
  /* webpackChunkName: "_locale-your-locale-js"*/
  /* webpackMode: "eager" */
  '@angular/common/locales/pt'
).then(m => storeLocaleData(m.default, 'pt-BR'));
import { provideAbpThemeShared, withValidationBluePrint } from '@abp/ng.theme.shared';
import {FcmService} from "./services/fcm.service";
import {SharedModule} from "./shared/shared.module";
import { CustomToasterService } from './toaster/toaster.service';
import { OfflineBannerComponent } from './offline-banner/offline-banner.component';
import { RefreshTokenResilienceInterceptor } from './services/refresh-token-resilience.interceptor';
import { REFRESH_TOKEN_ERROR_FILTER_PROVIDER } from './services/oauth-error-filter-registration';
import { BIOMETRIC_SESSION_SYNC_PROVIDER } from './login/services/biometric-session-sync.service';
import { SafeTitleStrategy } from './services/safe-title-strategy';
import { GALILEU_SAIDAS_ROUTE_PROVIDER } from './galileu-saidas/providers/galileu-saidas-route.provider';
import { CURADORIA_OFERTAS_ROUTE_PROVIDER } from './curadoria-ofertas/providers/curadoria-ofertas-route.provider';

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CoreModule.forRoot({
      environment,
      registerLocaleFn: registerLocale(),
    }),
    AbpOAuthModule.forRoot(),
    ThemeSharedModule.forRoot(),
    AccountAdminConfigModule.forRoot(),
    AccountPublicConfigModule.forRoot(),
    IdentityConfigModule.forRoot(),
    LanguageManagementConfigModule.forRoot(),
    SaasConfigModule.forRoot(),
    AuditLoggingConfigModule.forRoot(),
    OpeniddictproConfigModule.forRoot(),
    TextTemplateManagementConfigModule.forRoot(),
    SettingManagementConfigModule.forRoot(),
    ToastrModule.forRoot({
      timeOut: 10000,
      positionClass: 'toast-top-center',
    }),
    CommercialUiConfigModule.forRoot(),
    FeatureManagementModule.forRoot(),
    GdprConfigModule.forRoot({
      privacyPolicyUrl: 'gdpr-cookie-consent/privacy',
      cookiePolicyUrl: 'gdpr-cookie-consent/cookie',
    }),
    InternetConnectionStatusComponent,
    OfflineBannerComponent,
    ThemeLeptonXModule.forRoot(),
    TopMenuLayoutModule.forRoot(),
    NgSelectModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      // Desliga o SW no app nativo: em Capacitor os assets vêm de file://, então
      // o SW não ajuda e ainda arrisca servir bundle velho / tela branca
      // pós-update. Fica ligado só na web (PWA) em produção.
      enabled: !isDevMode() && !Capacitor.isNativePlatform(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    IonicModule.forRoot({}),
    SaasModule,
    SharedModule,
  ],
  providers: [
    provideAbpThemeShared(
      withValidationBluePrint({
        minDateToday: 'A data de término não pode ser anterior ao dia atual.'
      })
    ),
    APP_ROUTE_PROVIDER,
    ROTAS_ROTA_ROUTE_PROVIDER,
    OFERTAS_ABERTAS_ROUTE_PROVIDER,
    OFERTAS_OFERTA_ROUTE_PROVIDER,
    GALILEU_CREDENTIALS_GALILEU_CREDENTIAL_ROUTE_PROVIDER,
    GALILEU_SAIDAS_ROUTE_PROVIDER,
    CURADORIA_OFERTAS_ROUTE_PROVIDER,
    MANAGE_PROFILE_TAB_PROVIDER,
    USUARIOS_ACESSOS_TAB_PROVIDER,
    PROFILE_PICTURE_ROUTE_PROVIDER,
    GalileuCredentialDetailViewService,
    GalileuCredentialViewService,
    ListService,
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: AcceptLanguageInterceptor,
      multi: true,
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: TitleStrategy, useClass: SafeTitleStrategy },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorReportingInterceptor,
      multi: true,
    },
    // Refresh resiliente da sessao: retry do /connect/token em blip de rede
    // antes de o ABP apagar a sessao, mais o filtro como 2a linha de defesa.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RefreshTokenResilienceInterceptor,
      multi: true,
    },
    REFRESH_TOKEN_ERROR_FILTER_PROVIDER,
    // Mantem o cofre biometrico em sincronia com a rotacao do refresh token.
    BIOMETRIC_SESSION_SYNC_PROVIDER,
    { provide: 'MobileListService', useClass: ListService },
    { provide: 'MobileListOfertasService', useClass: ListService },
    { provide: 'MobileListRotasService', useClass: ListService },
    OFERTA_RECORDS_OFERTA_RECORD_ROUTE_PROVIDER,
    API_STATUSES_API_STATUS_ROUTE_PROVIDER,
    WHATS_APP_CONTACTS_WHATS_APP_CONTACT_ROUTE_PROVIDER,
    VIAGEM_ROUTE_PROVIDER,
    GERENCIAMENTO_VIAGEM_ROUTE_PROVIDER,
    FLEET_SCORE_ROUTE_PROVIDER,
    FATURAMENTO_ROUTE_PROVIDER,
    BOLSAO_ROUTE_PROVIDER,
    FLOWPAY_ROUTE_PROVIDER,
    CONFIRMACAO_ENTREGAS_ROUTE_PROVIDER,
    FcmService
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
